import { useState } from 'react';
import Dropzone from 'react-dropzone';
import CropImage from '../../components/crop-image/CropImage';
import { PrimaryButton, TaskButton } from '@sb1/ffe-buttons-react';
import { Icon } from '@sb1/ffe-icons-react';
import add from '@sb1/ffe-icons/icons/open/300/md/add.svg';
import image from '@sb1/ffe-icons/icons/open/300/md/image.svg';
import styles from './editable-image-button.module.less';
import { compressImage } from '../../utils/compress-image-utils';

const rejectStyle = {
    borderStyle: 'solid',
    backgroundColor: '#fdd',
};

const EditableImageButton = ({
    className,
    isCropping,
    tmpImageUrl,
    onDropRejected,
    updateImage,
    cropHandler,
    aspect = 1,
    startWidth = 100,
    buttonTask,
    hasImage = false,
    isFetchingImageText = 'Laster opp…',
    hasImageText = 'Endre bilde',
    noImageText = 'Velg bilde',
}) => {
    const getButtonText = ({ isFetchingImage, hasImage }) => {
        if (isFetchingImage) {
            return isFetchingImageText;
        } else if (hasImage) {
            return hasImageText;
        }

        return noImageText;
    };

    const [showCropWindow, setShowCropWindow] = useState(false);
    const [isFetchingImage, setIsFetchingImage] = useState(false);
    const [isCroppingImage, setIsCroppingImage] = useState(isCropping);
    const buttonText = getButtonText({ isFetchingImage, hasImage });

    const button = buttonTask ? (
        <TaskButton type={'button'} icon={<Icon fileUrl={add} />} className={className}>
            {buttonText}
        </TaskButton>
    ) : (
        <PrimaryButton leftIcon={<Icon fileUrl={image} />} type={'button'} className={className}>
            {buttonText}
        </PrimaryButton>
    );

    const onDropAccepted = (files) => {
        if (!files || files.length === 0) {
            return;
        }

        setIsFetchingImage(true);

        compressImage(files[0])
            .then((compressedFile) => updateImage(compressedFile))
            .then(() => {
                setShowCropWindow(true);
                setIsFetchingImage(false);
            })
            .catch(() => {
                setIsFetchingImage(false);
                setShowCropWindow(false);
            });
    };

    const onSaveCropped = (crop) => {
        setIsCroppingImage(true);
        cropHandler(crop)
            .then(() => {
                setShowCropWindow(false);
            })
            .catch(() => {})
            .finally(() => {
                setIsCroppingImage(false);
            });
    };

    const cancelCropButtonHandler = () => {
        setIsCroppingImage(false);
        setShowCropWindow(false);
    };

    const cropping = (
        <CropImage
            isOpen={showCropWindow && tmpImageUrl}
            cropImageHandler={onSaveCropped}
            cancelButtonHandler={cancelCropButtonHandler}
            imageUrl={tmpImageUrl}
            isSavingCroppedImage={isCroppingImage}
            startWidth={startWidth}
            aspect={aspect}
        />
    );

    return (
        <>
            <Dropzone
                inputProps={{ id: 'ImageInput' }}
                onDropAccepted={onDropAccepted}
                onDropRejected={onDropRejected}
                multiple={false}
                activeStyle={null}
                activeClassName={styles.dropzoneActive}
                rejectStyle={rejectStyle}
                className={styles.dropzone}
                accept={{
                    'image/jpeg': ['.jpeg', '.jpg'],
                    'image/jpg': ['.jpg'],
                    'image/png': ['.png'],
                    'image/webp': ['.webp'],
                }}
            >
                {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        {button}
                    </div>
                )}
            </Dropzone>
            {cropping}
        </>
    );
};

export default EditableImageButton;
